import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Menu, X, Sparkles, ShoppingBag, ChevronDown, Compass, Search, User, LogOut, LayoutDashboard, Shield, MessagesSquare, Building2, Store, CalendarDays } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import NotificationBell from "@/components/NotificationBell";
import ContextSwitcher from "@/components/ContextSwitcher";
import api from "@/lib/api";
import { buildNav } from "@/lib/navLayout";


const slug = (label) => label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "").split("-")[0];
const fullSlug = (label) => label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "");

// Personal wellness links are hidden from purely operational roles.
const OPERATIONAL_ROLES = ["admin", "staff", "council", "location_manager"];

// The workspace button adapts to the member's role. Business-builder tools
// (Enhancer Hub, Location OS, Admin, Council) are NEVER shown to discovery /
// community members — they only ever see "My Dashboard".
function workspaceFor(user) {
  if (!user) return null;
  const r = user.role;
  if (["admin", "staff"].includes(r)) return { label: "Admin", to: "/admin" };
  if (r === "council") return { label: "Council", to: "/council" };
  if (r === "enhancer") return { label: "Enhancer Hub", to: "/dashboard" };
  if (r === "location_manager") return { label: "Location OS", to: "/dashboard" };
  return { label: "My Dashboard", to: "/dashboard" };
}

// Every workspace this user can jump to — so admins who also manage a center
// (e.g. Chris) can toggle between Admin, their Center, Front Desk and Profile.
function workspacesFor(user) {
  if (!user) return [];
  const r = user.role;
  const out = [];
  if (["admin", "staff"].includes(r)) out.push({ label: "Admin", to: "/admin", icon: Shield });
  if (r === "council") out.push({ label: "Council", to: "/council", icon: Shield });
  if (user.manages_location_id || r === "location_manager") out.push({ label: "My Center", to: "/manager", icon: Building2 });
  else if (["admin", "staff"].includes(r)) out.push({ label: "Manage a Center", to: "/manager", icon: Building2 });
  if (["admin", "staff", "location_manager", "enhancer"].includes(r)) out.push({ label: "POS Register", to: "/pos", icon: Store });
  if (r === "enhancer") out.push({ label: "Enhancer Hub", to: "/dashboard", icon: LayoutDashboard });
  else if (user.is_enhancer) out.push({ label: "My Enhancer Dashboard", to: "/enhancer", icon: Sparkles });
  if (!["admin", "staff", "council", "location_manager"].includes(r)) out.push({ label: "My Dashboard", to: "/dashboard", icon: LayoutDashboard });
  return out;
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [exploreOpen, setExploreOpen] = useState(false);
  const [acctOpen, setAcctOpen] = useState(false);
  const { user, ready, logout } = useAuth();
  const { count, setOpen: setCartOpen } = useCart();
  const nav = useNavigate();

  const { data: customPages = [] } = useQuery({
    queryKey: ["nav-pages"],
    queryFn: () => api.get("/pages").then((r) => r.data.filter((p) => p.show_in_nav)),
  });
  const { data: navConfig, isError: navError } = useQuery({
    queryKey: ["nav-config"],
    queryFn: () => api.get("/nav-config").then((r) => r.data),
  });

  // Lay out only once auth and the saved menu are loaded, so links don't shift after first paint.
  const navReady = ready && (Array.isArray(navConfig) || navError);
  const { primaryLinks, exploreLinks } = navReady ? buildNav(navConfig || [], customPages) : { primaryLinks: [], exploreLinks: [] };
  const isMember = user && !OPERATIONAL_ROLES.includes(user.role);
  const { data: unread } = useQuery({
    queryKey: ["unread-count"],
    queryFn: () => api.get("/messages/unread-count").then((r) => r.data.count),
    enabled: !!user,
    refetchInterval: 30000,
  });
  const doLogout = () => { setAcctOpen(false); setOpen(false); logout(); nav("/"); };
  const ws = workspaceFor(user);
  const workspaces = workspacesFor(user);

  const CartButton = ({ testid }) => (
    <button onClick={() => setCartOpen(true)} data-testid={testid} className="relative grid place-items-center h-11 w-11 -m-1.5 text-white hover:text-[#F0C9B8] transition-colors">
      <ShoppingBag size={20} />
      {count > 0 && (
        <span data-testid="cart-count" className="absolute top-0.5 right-0.5 grid place-items-center h-4.5 w-4.5 min-w-[18px] px-1 rounded-full bg-[#D98E73] text-white text-[10px] font-semibold">{count}</span>
      )}
    </button>
  );

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#144F75]/95 border-b border-white/10">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 xl:px-6 2xl:px-10 h-20 flex flex-nowrap items-center justify-between gap-3 2xl:gap-4 overflow-visible">
        <Link to="/" data-testid="nav-logo" className="flex items-center shrink-0 min-w-[170px] xl:min-w-[190px]">
          <img src="https://customer-assets.emergentagent.com/job_eyw-admin-suite/artifacts/3ze0y6kq_EnhancingYourWorld%20site%20logo%20with%20transparent%20background%20web%20version.png"
            alt="Enhancing Your World" className="h-10 sm:h-12 2xl:h-14 w-auto max-w-[210px] object-contain object-left" />
        </Link>

        <nav className="hidden xl:flex flex-nowrap items-center gap-2 2xl:gap-5 flex-1 min-w-0 [justify-content:safe_center]">
          {!navReady && (
            <div className="flex items-center gap-4" data-testid="nav-skeleton" aria-hidden>
              {[56, 72, 96, 48, 64, 80, 56].map((w, i) => <span key={i} className="h-3 rounded-full bg-white/15 animate-pulse" style={{ width: w }} />)}
            </div>
          )}
          {navReady && primaryLinks.map((l) => (
            l.to === "/discover" ? (
              <NavLink key={l.to} to={l.to} data-testid="nav-discover"
                className={({ isActive }) => `flex items-center gap-1 text-xs 2xl:text-[13px] whitespace-nowrap px-3 py-1 rounded-full border transition-all ${isActive ? "bg-white text-[#144F75] border-white font-semibold" : "bg-white/10 text-white border-white/30 hover:bg-white/20"}`}>
                <Search size={11} /> {l.label}
              </NavLink>
            ) : (
              <NavLink key={l.to} to={l.to} data-testid={`nav-${slug(l.label)}`}
                className={({ isActive }) => `text-xs 2xl:text-[13px] whitespace-nowrap transition-colors hover:text-[#F0C9B8] ${isActive ? "text-white font-semibold" : "text-white/75"}`}>
                {l.label}
              </NavLink>
            )
          ))}
          {/* Explore dropdown */}
          {navReady && exploreLinks.length > 0 && <div className="relative" onMouseEnter={() => setExploreOpen(true)} onMouseLeave={() => setExploreOpen(false)}>
            <button data-testid="nav-explore" className="flex items-center gap-1 text-xs 2xl:text-[13px] whitespace-nowrap text-white/75 hover:text-[#F0C9B8] transition-colors">
              Explore <ChevronDown size={14} className={`transition-transform ${exploreOpen ? "rotate-180" : ""}`} />
            </button>
            {exploreOpen && (
              <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-56 z-50" data-testid="nav-explore-menu">
                <div className="bg-white rounded-2xl shadow-xl border border-[#E5E0D8] p-2">
                  {exploreLinks.map((l) => (
                    <NavLink key={l.to} to={l.to} data-testid={`nav-${slug(l.label)}`}
                      className={({ isActive }) => `block px-4 py-2.5 rounded-xl text-sm transition-colors hover:bg-[#144F75]/[0.05] ${isActive ? "text-[#144F75] font-semibold" : "text-[#4A5D6B]"}`}>
                      {l.label}
                    </NavLink>
                  ))}
                </div>
              </div>
            )}
          </div>}
        </nav>

        <div className="hidden xl:flex flex-nowrap items-center gap-2 2xl:gap-3 shrink-0">
          <CartButton testid="nav-cart-btn" />
          {user && (
            <Link to="/messages" data-testid="nav-messages-btn" className="relative grid place-items-center h-11 w-11 -m-1.5 text-white hover:text-[#F0C9B8] transition-colors">
              <MessagesSquare size={20} />
              {unread > 0 && <span className="absolute top-0.5 right-0.5 grid place-items-center h-4.5 w-4.5 min-w-[18px] px-1 rounded-full bg-[#D98E73] text-white text-[10px] font-semibold" data-testid="nav-messages-badge">{unread}</span>}
            </Link>
          )}
          {user && <NotificationBell />}
          <Link to="/gauge/app" data-testid="nav-start-gauge" className="eyw-btn !bg-[#D98E73] hover:!bg-[#c67a60] !px-4 2xl:!px-5 !py-2 text-sm whitespace-nowrap"><Compass size={16} /> Start GAUGE</Link>
          {user ? (
            <div className="relative" onMouseEnter={() => setAcctOpen(true)} onMouseLeave={() => setAcctOpen(false)}>
              <button data-testid="nav-account-btn" className="flex items-center gap-2 eyw-btn-ghost !border-white/40 !text-white hover:!bg-white hover:!text-[#144F75] !px-3 !py-2 text-sm">
                <span className="grid place-items-center h-6 w-6 rounded-full bg-[#D98E73] text-white text-xs">{(user.name || user.email || "?").charAt(0).toUpperCase()}</span>
                <ChevronDown size={14} className={`transition-transform ${acctOpen ? "rotate-180" : ""}`} />
              </button>
              {acctOpen && (
                <div className="absolute right-0 top-full pt-3 w-72 z-50" data-testid="nav-account-menu">
                  <div className="bg-white rounded-2xl shadow-xl border border-[#E5E0D8] p-2">
                    <p className="px-4 py-2 text-xs text-[#8FA89B] truncate">{user.email}</p>
                    <div className="px-4 -mt-2"><ContextSwitcher mobile /></div>
                    {workspaces.length > 1 && <p className="px-4 pt-1 pb-1 text-[10px] uppercase tracking-wider text-[#B5B0A6]">Workspaces</p>}
                    {workspaces.map((w) => (
                      <button key={w.to} onClick={() => { setAcctOpen(false); nav(w.to); }} data-testid={`nav-ws-${fullSlug(w.label)}`} className="w-full text-left flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-sm text-[#4A5D6B] hover:bg-[#144F75]/[0.05]"><w.icon size={16} /> {w.label}</button>
                    ))}
                    <div className="my-1 border-t border-[#E5E0D8]/70" />
                    {isMember && <Link to="/coach" onClick={() => setAcctOpen(false)} className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-sm text-[#4A5D6B] hover:bg-[#144F75]/[0.05]"><Compass size={16} /> Daily Coach</Link>}
                    <Link to="/messages" onClick={() => setAcctOpen(false)} data-testid="nav-account-messages" className="flex items-center justify-between gap-2.5 px-4 py-2.5 rounded-xl text-sm text-[#4A5D6B] hover:bg-[#144F75]/[0.05]"><span className="flex items-center gap-2.5"><MessagesSquare size={16} /> Messages</span>{unread > 0 && <span className="bg-[#D98E73] text-white text-[10px] rounded-full h-5 min-w-5 px-1.5 grid place-items-center" data-testid="nav-unread-badge">{unread}</span>}</Link>
                    <Link to="/my-calendar" onClick={() => setAcctOpen(false)} data-testid="nav-account-calendar" className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-sm text-[#4A5D6B] hover:bg-[#144F75]/[0.05]"><CalendarDays size={16} /> My Calendar</Link>
                    <Link to="/settings/profile" onClick={() => setAcctOpen(false)} data-testid="nav-account-profile" className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-sm text-[#4A5D6B] hover:bg-[#144F75]/[0.05]"><User size={16} /> Profile &amp; Privacy</Link>
                    <button onClick={doLogout} data-testid="nav-logout-btn" className="w-full text-left flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-sm text-[#B4463A] hover:bg-[#B4463A]/[0.06]"><LogOut size={16} /> Log out</button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <>
              <button data-testid="nav-login-btn" onClick={() => nav("/login")} className="eyw-btn-ghost !border-white/40 !text-white hover:!bg-white hover:!text-[#144F75] !px-4 !py-2 text-sm">Sign in</button>
              <Link to="/join" data-testid="nav-join-btn" className="eyw-btn-ghost !px-4 !py-2 text-sm !border-[#E6B39F]/60 !text-[#F0C9B8] hover:!bg-[#D98E73] hover:!text-white">Join</Link>
            </>
          )}
        </div>

        <div className="xl:hidden flex items-center gap-1.5 shrink-0">
          <CartButton testid="nav-cart-btn-mobile" />
          {user && (
            <Link to="/messages" data-testid="nav-messages-btn-mobile" className="relative grid place-items-center h-11 w-11 -m-1 text-white hover:text-[#F0C9B8] transition-colors">
              <MessagesSquare size={20} />
              {unread > 0 && <span className="absolute top-0.5 right-0.5 grid place-items-center h-4.5 w-4.5 min-w-[18px] px-1 rounded-full bg-[#D98E73] text-white text-[10px] font-semibold">{unread}</span>}
            </Link>
          )}
          {user && <NotificationBell />}
          <button
            data-testid="nav-mobile-toggle"
            className="text-white grid place-items-center h-11 w-11 -mr-2"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="xl:hidden border-t border-[#E5E0D8] bg-[#F9F7F3] px-6 py-4 flex flex-col gap-1 max-h-[75vh] overflow-y-auto">
          <Link to="/gauge/app" onClick={() => setOpen(false)} data-testid="nav-mobile-start-gauge" className="eyw-btn !py-2.5 text-sm justify-center mb-2"><Compass size={16} /> Start GAUGE Exploration</Link>
          {!navReady && <div className="space-y-3 py-3" data-testid="nav-mobile-skeleton" aria-hidden>{[1, 2, 3, 4].map((i) => <div key={i} className="h-4 w-40 rounded-full bg-[#E5E0D8] animate-pulse" />)}</div>}
          {primaryLinks.map((l) => (
            l.to === "/discover" ? (
              <NavLink key={l.to} to={l.to} onClick={() => setOpen(false)} data-testid="nav-mobile-discover" className="flex items-center gap-2 text-[#144F75] font-medium py-3 border-b border-[#E5E0D8]/60"><Search size={14} /> {l.label}</NavLink>
            ) : (
              <NavLink key={l.to} to={l.to} onClick={() => setOpen(false)} data-testid={`nav-mobile-${slug(l.label)}`} className="text-[#4A5D6B] py-3 border-b border-[#E5E0D8]/60">{l.label}</NavLink>
            )
          ))}
          {exploreLinks.length > 0 && <p className="text-xs uppercase tracking-wider text-[#8FA89B] pt-3 pb-1">Explore</p>}
          {exploreLinks.map((l) => (
            <NavLink key={l.to} to={l.to} onClick={() => setOpen(false)} data-testid={`nav-mobile-${slug(l.label)}`} className="text-[#4A5D6B] py-3 pl-3">{l.label}</NavLink>
          ))}
          <NavLink to="/help" onClick={() => setOpen(false)} className="text-[#4A5D6B] py-3 border-t border-[#E5E0D8]/60 mt-2">Help</NavLink>

          {user ? (
            <div className="flex flex-col gap-1 pt-3 border-t border-[#E5E0D8] mt-2">
              <Link to="/settings/profile" onClick={() => setOpen(false)} data-testid="nav-mobile-profile-name" className="text-sm text-[#1A242B] font-medium truncate hover:text-[#144F75]">
                {user.name || user.email}
                <span className="block text-xs font-normal text-[#8FA89B] truncate">{user.email}</span>
              </Link>
              <ContextSwitcher mobile />
              {workspaces.map((w) => (
                <button key={w.to} onClick={() => { setOpen(false); nav(w.to); }} data-testid={`nav-mobile-ws-${fullSlug(w.label)}`} className="text-left text-[#144F75] py-2 flex items-center gap-2"><w.icon size={15} /> {w.label}</button>
              ))}
              <Link to="/my-calendar" onClick={() => setOpen(false)} className="text-[#4A5D6B] py-2">My Calendar</Link>
              <Link to="/settings/profile" onClick={() => setOpen(false)} className="text-[#4A5D6B] py-2">Profile &amp; Privacy</Link>
              <Link to="/messages" onClick={() => setOpen(false)} data-testid="nav-mobile-messages" className="text-[#4A5D6B] py-2 flex items-center gap-2"><MessagesSquare size={15} /> Messages {unread > 0 && <span className="bg-[#D98E73] text-white text-[10px] rounded-full h-5 min-w-5 px-1.5 grid place-items-center">{unread}</span>}</Link>
              <button onClick={doLogout} data-testid="nav-mobile-logout" className="text-left text-[#B4463A] py-2 flex items-center gap-2"><LogOut size={15} /> Log out</button>
            </div>
          ) : (
            <div className="flex gap-3 pt-3 border-t border-[#E5E0D8] mt-2">
              <button onClick={() => { setOpen(false); nav("/login"); }} data-testid="nav-mobile-login" className="eyw-btn-ghost !py-2 text-sm flex-1">Sign in</button>
              <button onClick={() => { setOpen(false); nav("/join"); }} className="eyw-btn !py-2 text-sm flex-1">Join</button>
            </div>
          )}
        </div>
      )}
    </header>
  );
}
